import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './Auth.scss';
import { API_CONFIG } from '../../config';
import { Helmet } from 'react-helmet';
import ProfilePictureUpload from '../ProfilePictureUpload';

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [submittedRecipes, setSubmittedRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('favorites'); // 'favorites' or 'submitted'
  const [isEditingAbout, setIsEditingAbout] = useState(false);
  const [aboutText, setAboutText] = useState('');
  const [savingAbout, setSavingAbout] = useState(false);

  const fetchUserData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Fetch user profile
      const userResponse = await fetch(`${API_CONFIG.BACKEND_URL}/api/user/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!userResponse.ok) {
        throw new Error('Failed to fetch user profile');
      }

      const userData = await userResponse.json();
      setUser(userData);
      setAboutText(userData.about || '');

      // Fetch favorites
      const favoritesResponse = await fetch(`${API_CONFIG.BACKEND_URL}/api/user/favorites`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!favoritesResponse.ok) {
        throw new Error('Failed to fetch favorites');
      }

      const favoritesData = await favoritesResponse.json();
      setFavorites(favoritesData);

      // Fetch submitted recipes
      const recipesResponse = await fetch(`${API_CONFIG.BACKEND_URL}/api/recipes/my-recipes`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!recipesResponse.ok) {
        throw new Error('Failed to fetch submitted recipes');
      }

      const recipesData = await recipesResponse.json();
      setSubmittedRecipes(recipesData);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const removeFavorite = async (recipeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.BACKEND_URL}/api/user/favorites/${recipeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to remove from favorites');
      }

      // Remove from local state
      setFavorites(favorites.filter(fav => fav.recipeId !== recipeId));
      toast.success('Recipe removed from favorites');
    } catch (error) {
      toast.error('Error removing recipe from favorites');
      console.error('Error removing favorite:', error);
    }
  };

  const handleProfilePictureUpdate = async (imageUrl) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.BACKEND_URL}/api/user/profile-picture`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ profilePicture: imageUrl })
      });

      if (!response.ok) {
        throw new Error('Failed to update profile picture');
      }

      // Update local user state
      setUser(prevUser => ({
        ...prevUser,
        profilePicture: imageUrl
      }));

      toast.success('Profile picture updated successfully!');
    } catch (error) {
      toast.error('Error updating profile picture');
      console.error('Error updating profile picture:', error);
    }
  };

  const handleAboutUpdate = async () => {
    try {
      setSavingAbout(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.BACKEND_URL}/api/user/about`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ about: aboutText })
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // Handle non-JSON response (likely HTML error page)
        const textResponse = await response.text();
        console.error('Non-JSON response:', textResponse);
        throw new Error('Server returned an invalid response. Please try again.');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update about description');
      }

      const data = await response.json();

      // Update local user state
      setUser(prevUser => ({
        ...prevUser,
        about: data.about
      }));

      setIsEditingAbout(false);
      toast.success('About description updated successfully!');
    } catch (error) {
      toast.error(error.message);
      console.error('Error updating about description:', error);
    } finally {
      setSavingAbout(false);
    }
  };

  const handleCancelEdit = () => {
    setAboutText(user?.about || '');
    setIsEditingAbout(false);
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="loading">Loading profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-container">
        <div className="error-message">{error}</div>
        <button onClick={() => navigate('/login')} className="auth-button">
          Return to Login
        </button>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <Helmet>
        <title>Profile | Zorbas' Kitchen</title>
        <meta name="description" content="Your profile and recipes" />
      </Helmet>

      <h2>Profile</h2>
      {user && (
        <div className="profile-info">
          <div className="profile-section">
            <h3>User Information</h3>
            <div className="profile-picture-section">
              <ProfilePictureUpload
                onImageUpload={handleProfilePictureUpdate}
                currentImage={user.profilePicture}
                className="profile-picture-upload"
              />
            </div>
            <p><strong>Username:</strong> {user.username}</p>
            <p><strong>Email:</strong> {user.email}</p>
            {user.isAdmin && (
              <p className="admin-badge">Administrator</p>
            )}
          </div>

          <div className="profile-section">
            <h3>About Me</h3>
            {isEditingAbout ? (
              <div className="about-edit">
                <textarea
                  value={aboutText}
                  onChange={(e) => setAboutText(e.target.value)}
                  placeholder="Tell us about yourself, your cooking experience, favorite cuisines, or anything you'd like to share..."
                  maxLength={500}
                  rows={4}
                  className="about-textarea"
                />
                <div className="about-edit-actions">
                  <span className="char-count">{aboutText.length}/500</span>
                  <div className="about-buttons">
                    <button 
                      onClick={handleCancelEdit} 
                      className="btn btn-secondary"
                      disabled={savingAbout}
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={handleAboutUpdate} 
                      className="btn btn-primary"
                      disabled={savingAbout}
                    >
                      {savingAbout ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="about-display">
                {user.about ? (
                  <p className="about-text">{user.about}</p>
                ) : (
                  <p className="about-placeholder">No description yet. Click edit to add one!</p>
                )}
                <button 
                  onClick={() => setIsEditingAbout(true)} 
                  className="btn btn-secondary edit-about-btn"
                >
                  Edit About
                </button>
              </div>
            )}
          </div>

          <div className="profile-actions">
            <button onClick={() => navigate('/submit-recipe')} className="btn btn-primary">
              Submit New Recipe
            </button>
            {user.isAdmin && (
              <button onClick={() => navigate('/admin/recipes')} className="btn btn-secondary">
                Manage Recipes
              </button>
            )}
          </div>

          <div className="profile-tabs">
            <button
              className={`tab-button ${activeTab === 'favorites' ? 'active' : ''}`}
              onClick={() => setActiveTab('favorites')}
            >
              Favorite Recipes
            </button>
            <button
              className={`tab-button ${activeTab === 'submitted' ? 'active' : ''}`}
              onClick={() => setActiveTab('submitted')}
            >
              My Submitted Recipes
            </button>
          </div>

          {activeTab === 'favorites' ? (
            <div className="profile-section">
              <h3>Favorite Recipes</h3>
              {favorites.length === 0 ? (
                <p>No favorite recipes yet</p>
              ) : (
                <div className="favorites-grid">
                  {favorites.map((recipe) => (
                    <div key={recipe.id} className="favorite-recipe">
                      <button
                        onClick={() => removeFavorite(recipe.recipeId)}
                        className="remove-favorite-x"
                        aria-label="Remove from favorites"
                      >
                        ×
                      </button>
                      <img src={recipe.image} alt={recipe.title} />
                      <h4>{recipe.title}</h4>
                      <button
                        onClick={() => navigate(`/result-item/${recipe.recipeId}?from=favorites`)}
                        className="view-recipe-btn"
                      >
                        View Recipe
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="profile-section">
              <h3>My Submitted Recipes</h3>
              {submittedRecipes.length === 0 ? (
                <p>No submitted recipes yet</p>
              ) : (
                <div className="submitted-recipes-grid">
                  {submittedRecipes.map((recipe) => (
                    <div key={recipe._id} className="submitted-recipe">
                      <img src={recipe.image} alt={recipe.title} />
                      <div className="recipe-info">
                        <h4>{recipe.title}</h4>
                        <p className={`status ${recipe.status}`}>
                          Status: {recipe.status.charAt(0).toUpperCase() + recipe.status.slice(1)}
                        </p>
                        {recipe.status === 'rejected' && recipe.rejectionReason && (
                          <p className="rejection-reason">
                            Reason: {recipe.rejectionReason}
                          </p>
                        )}
                        <div className="recipe-actions">
                          <button
                            onClick={() => navigate(`/result-item/${recipe._id}?from=profile`)}
                            className="btn btn-primary"
                          >
                            View Recipe
                          </button>
                          <button
                            onClick={() => navigate(`/edit-recipe/${recipe._id}`)}
                            className="btn btn-secondary"
                          >
                            Edit Recipe
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      )}
    </div>
  );
};

export default Profile; 